import {
  BodyText,
  Button,
  CheckIcon,
  CloseIcon,
  File,
  IconList,
  IdentityVerificationNeededIllu,
  Input,
  Modal,
  PrimitiveText,
} from '@farol-ds/react'
import { COMPONENT_NAME } from '@jusbrasil-web/shared-ga4-events'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import classNames from 'classnames'
import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { COMPONENT_RENDER_EVENT_PROPS, GA4EventHandlers } from '../../../hooks'
import { fileToBase64 } from '../../../utils'
import { DropFileZone } from '../../id-document-validation/components/drop-file-zone'
import { UploadSourceSheet } from '../components/upload-source-sheet'
import { ACCEPTED_FILE_INPUT, isValidEnrollmentDocumentFile } from '../constants'
import { useStudentEnrollmentValidationContext } from '../context'
import { Steps } from '../enums'
import styles from './steps.module.scss'

interface UploadInstructionsProps {
  gA4EventHandlers?: GA4EventHandlers
}

export function UploadInstructions(props: UploadInstructionsProps) {
  const { gA4EventHandlers } = props
  const { setCurrentStep, sharedData, setSharedData, eventData, viewer, texts } =
    useStudentEnrollmentValidationContext()

  const [file, setFile] = useState<File | null>(null)
  const [isFileValid, setIsFileValid] = useState(true)
  const [isSourceSheetOpen, setIsSourceSheetOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const pid = viewer?.pid

  const title =
    texts?.uploadInstructions?.title ?? 'Para finalizar, envie seu comprovante de matrícula'
  const description =
    texts?.uploadInstructions?.description ??
    'Geralmente disponível no Portal do Aluno ou sistema acadêmico da sua instituição.'
  const submitLabel = texts?.uploadInstructions?.submitButtonText ?? 'Enviar documento'

  useEffect(() => {
    sendMetrics('UserAccount.StudentEnrollmentProofViewed', `${pid}`, { ...eventData })
    gA4EventHandlers?.sendComponentRender({
      componentClass: null,
      componentName: COMPONENT_NAME.DOCUMENT_VALIDATION,
      renders: COMPONENT_RENDER_EVENT_PROPS.STUDENT_ENROLLMENT_UPLOAD_INSTRUCTIONS,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const storeDocument = async (selectedFile: File) => {
    const documentData = await fileToBase64(selectedFile)
    setSharedData({
      ...sharedData,
      documentFile: selectedFile,
      documentData,
      documentName: selectedFile.name,
      mimeType: selectedFile.type || undefined,
    })
  }

  const handleFileReceived = async (selectedFile: File) => {
    if (!isValidEnrollmentDocumentFile(selectedFile)) {
      sendMetrics('UserAccount.StudentEnrollmentProofFormatError', `${pid}`, {
        ...eventData,
        metadata: { ...eventData?.metadata, mimeType: selectedFile.type },
      })
      setIsFileValid(false)
      setFile(null)
      return
    }

    setFile(selectedFile)
    setIsFileValid(true)
    await storeDocument(selectedFile)
    setCurrentStep(Steps.DOCUMENT_PREVIEW)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) {
      return
    }

    handleFileReceived(selectedFile)
  }

  const handleDeskSubmit = async () => {
    if (!file) {
      return
    }

    sendMetrics('UserAccount.StudentEnrollmentProofSubmitted', `${pid}`, {
      ...eventData,
      metadata: { ...eventData?.metadata, mimeType: file.type },
    })
    await storeDocument(file)
    setCurrentStep(Steps.VALIDATING_ENROLLMENT)
  }

  const handleMobileSubmit = () => {
    if (file) {
      setCurrentStep(Steps.DOCUMENT_PREVIEW)
      return
    }

    sendMetrics('UserAccount.StudentEnrollmentProofUploadViewed', `${pid}`, { ...eventData })
    setIsSourceSheetOpen(true)
  }

  const validDocumentsContent = (
    <>
      <IdentityVerificationNeededIllu className={styles.illustration} />
      <BodyText>
        Documentos válidos: <strong>Comprovante de matrícula</strong>
      </BodyText>
    </>
  )

  return (
    <>
      <Modal.Header>
        <Modal.HeaderTitle>{title}</Modal.HeaderTitle>
        <Modal.HeaderDescription>{description}</Modal.HeaderDescription>
      </Modal.Header>
      <Modal.Body data-testid="upload-instructions-step">
        <Input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_FILE_INPUT}
          onChange={handleFileChange}
          className={styles.hiddenFileInput}
        />
        <div className={styles.mobileFileBox}>{validDocumentsContent}</div>
        <div className={styles.deskFileUpload}>
          {file ? (
            <File rightIcon={<CloseIcon onClick={() => setFile(null)} />}>
              <File.Content>
                <File.Label>{file.name}</File.Label>
              </File.Content>
            </File>
          ) : (
            <DropFileZone
              className={classNames(styles.dropArea, { [styles.dropAreaError]: !isFileValid })}
              isFileValid={isFileValid}
              onFileDropped={handleFileReceived}
              onDropZoneClick={() => {
                sendMetrics('UserAccount.StudentEnrollmentProofUploadClicked', `${pid}`, {
                  ...eventData,
                  metadata: { ...eventData?.metadata, select_type: 'imageBox' },
                })
                inputRef.current?.click()
              }}
            >
              {validDocumentsContent}
              <PrimitiveText className={styles.dropAreaHighlight}>Anexar arquivo</PrimitiveText>
            </DropFileZone>
          )}
        </div>
        <IconList size="md" className={styles.checklist}>
          <IconList.Item icon={<CheckIcon className={styles.checkIcon} />}>
            <span>
              <strong>Todo o documento</strong> deve aparecer na foto
            </span>
          </IconList.Item>
          <IconList.Item icon={<CheckIcon className={styles.checkIcon} />}>
            <span>
              Seus dados devem estar <strong>legíveis</strong>
            </span>
          </IconList.Item>
          <IconList.Item icon={<CheckIcon className={styles.checkIcon} />}>
            <span>
              Retire o documento do plástico e esteja em um <strong>local iluminado</strong>
            </span>
          </IconList.Item>
          <IconList.Item
            className={isFileValid ? undefined : styles.errorListItem}
            icon={
              isFileValid ? (
                <CheckIcon className={styles.checkIcon} />
              ) : (
                <CloseIcon className={styles.errorIcon} />
              )
            }
          >
            <span>
              Formatos válidos: <strong>jpeg</strong>, <strong>png</strong> ou <strong>pdf</strong>
            </span>
          </IconList.Item>
        </IconList>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="primary" onClick={handleMobileSubmit} className={styles.mobileSubmitBtn}>
          {submitLabel}
        </Button>
        <Button
          kind="primary"
          onClick={handleDeskSubmit}
          disabled={!file}
          className={styles.deskSubmitBtn}
        >
          {submitLabel}
        </Button>
      </Modal.Footer>
      <UploadSourceSheet
        open={isSourceSheetOpen}
        onClose={() => setIsSourceSheetOpen(false)}
        onFileSelected={handleFileReceived}
        onSelectSource={(source) =>
          sendMetrics('UserAccount.StudentEnrollmentProofUploadClicked', `${pid}`, {
            ...eventData,
            metadata: { ...eventData?.metadata, select_type: source },
          })
        }
      />
    </>
  )
}
